import { DateInput, DatePicker, DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { addDays, addMonths, addWeeks, subDays } from 'date-fns';
import { isNumber } from 'lodash';
import { FC, useCallback, useMemo } from 'react';
import TextInput from 'src/components/TextInput';
import ClickableTooltip from 'src/components/design-system/ClickableTooltip';
import useViewCalendarYear from 'src/hooks/useViewCalendarYear';
import { SetState } from 'src/types/generic';
import { informationIcon } from 'src/utils/icons';
import { NoNCalendarYear } from './FiscalYearsTableGridToolbar';

interface NoNCalendarYearModalProps {
  nonCalendarYear: NoNCalendarYear;
  setNoNCalendarYear: SetState<NoNCalendarYear>;
  isMonthValidationFailed: boolean;
  hasNoOfPeriodsErrors: boolean;
  hasPeriodsLengthErrors: boolean;
}

const NoNCalendarYearModal: FC<NoNCalendarYearModalProps> = ({
  nonCalendarYear,
  setNoNCalendarYear,
  isMonthValidationFailed,
  hasNoOfPeriodsErrors,
  hasPeriodsLengthErrors,
}) => {
  const { latestFYPeriod } = useViewCalendarYear();
  const dropdownPeriodTypes = useMemo(
    () => [
      { id: 0, type: 'Day(s)' },
      { id: 1, type: 'Week(s)' },
      { id: 2, type: 'Month(s)' },
    ],
    []
  );

  const handleEndDateCalc = useCallback(
    (
      date: Date,
      noOfPeriods: number,
      periodLength: number,
      periodType: 'Week(s)' | 'Month(s)' | 'Day(s)'
    ) => {
      const total = noOfPeriods * periodLength;
      let calcDate = date;
      if (periodType === 'Day(s)') {
        calcDate = subDays(addDays(date, total), total === 0 ? 0 : 1);
      } else if (periodType === 'Week(s)') {
        calcDate = subDays(addWeeks(date, total), total === 0 ? 0 : 1);
      } else {
        calcDate = subDays(addMonths(date, total), 1);
      }
      if (!total || !periodType || !date) {
        return date;
      }

      return calcDate;
    },
    []
  );

  const handleStartDateChanges = useCallback(
    (event: DatePickerChangeEvent) => {
      const date = event?.value || new Date();
      setNoNCalendarYear((prev) => ({
        ...prev,
        startDate: date,
        endDate: handleEndDateCalc(
          date,
          prev?.noOfPeriods,
          prev?.periodLength,
          prev?.periodType?.type
        ),
      }));
    },
    [handleEndDateCalc, setNoNCalendarYear]
  );

  const handleInputChange = useCallback(
    (event: TextBoxChangeEvent, key: 'noOfPeriods' | 'periodLength') => {
      setNoNCalendarYear((prev) => {
        const value = isNumber(parseFloat(event.value as string))
          ? Number(event.value) < 0 || event.value === '-'
            ? 0
            : event.value
          : event.value;
        return {
          ...prev,
          [key]: value,
          endDate: handleEndDateCalc(
            prev?.startDate,
            key === 'noOfPeriods' ? (value as number) : prev?.noOfPeriods,
            key === 'periodLength' ? (value as number) : prev?.periodLength,
            prev?.periodType?.type
          ),
        };
      });
    },
    [handleEndDateCalc, setNoNCalendarYear]
  );

  const handlePeriodTypeChange = useCallback(
    (event: DropDownListChangeEvent) => {
      setNoNCalendarYear((prev) => ({
        ...prev,
        periodType: event.value,
        endDate: handleEndDateCalc(
          prev.startDate,
          prev?.noOfPeriods,
          prev?.periodLength,
          event.value?.type
        ),
      }));
    },
    [handleEndDateCalc, setNoNCalendarYear]
  );

  return (
    <div>
      <ul className="text-xs bg-[#d0e9f3] text-secondary-hover list-disc pl-8 pr-2 py-2 mb-2">
        <li>Please select the number periods and period length for the new year</li>
        <li>
          Once the year is created, you can edit the individual period start and end dates to ensure
          accuracy
        </li>
      </ul>
      <div className="mb-2">
        <label className="text-md">Start Date</label>
        {latestFYPeriod?.year ? (
          <div className="relative">
            <DateInput
              disabled={true}
              key={'startDate'}
              value={nonCalendarYear.startDate}
              valid={true}
              size="small"
            />
            <ClickableTooltip tooltipMessage="Determined by previous fiscal year's end date">
              <span className="absolute top-1 right-2 cursor-pointer">{informationIcon}</span>
            </ClickableTooltip>
          </div>
        ) : (
          <DatePicker
            key={'startDate'}
            value={nonCalendarYear.startDate}
            onChange={handleStartDateChanges}
            size="small"
            valid={!isMonthValidationFailed && !!nonCalendarYear.startDate}
          />
        )}
      </div>
      <div className="mb-2">
        <label className="text-md">Number of Periods</label>
        <TextInput
          min={0}
          step={1}
          value={nonCalendarYear.noOfPeriods}
          onChange={(e) => handleInputChange(e, 'noOfPeriods')}
          type="number"
          size="small"
          valid={!hasNoOfPeriodsErrors}
        />
      </div>
      <div className="mb-2">
        <label className="text-md">Period Length</label>
        <div className="max-w-md mx-auto flex space-x-4">
          <div className="w-1/4">
            <TextInput
              min={0}
              step={1}
              value={nonCalendarYear.periodLength}
              onChange={(e) => handleInputChange(e, 'periodLength')}
              type="number"
              valid={!hasPeriodsLengthErrors}
            />
          </div>
          <div className="w-3/4">
            <DropDownList
              textField="type"
              dataItemKey="id"
              value={nonCalendarYear.periodType}
              onChange={handlePeriodTypeChange}
              data={dropdownPeriodTypes}
              size="small"
            />
          </div>
        </div>
      </div>
      <div className="mb-2">
        <label className="text-md">Your end date is</label>
        <div className="relative">
          <DateInput
            disabled={true}
            key={'endDate'}
            value={nonCalendarYear.endDate}
            valid={true}
            className="mr-2"
            size="small"
          />
          <ClickableTooltip tooltipMessage="Determined dynamically based on selected periods and period length">
            <span className="absolute top-1 right-2 cursor-pointer">{informationIcon}</span>
          </ClickableTooltip>
        </div>
      </div>
    </div>
  );
};

export default NoNCalendarYearModal;
